import { renderToStaticMarkup } from 'react-dom/server';
import { ConditionIcons } from 'components';

/**
 * @fileoverview Mark point and mark area plugins for ECharts visualization.
 * Provides weather icons, wind direction arrows, and threshold area highlighting.
 * @module chartPlugins/markPointPlugins
 */

/**
 * Creates colored mark areas for threshold/event visualization in Aware device charts.
 * Highlights regions based on threshold levels with different colors:
 * - Level 1: Yellow (rgba(255, 255, 0, 0.2))
 * - Level 2: Orange (rgba(255, 165, 0, 0.25))
 * - Level 3: Red (rgba(255, 0, 0, 0.3))
 *
 * @param {Array} series - ECharts series array with _rawData
 * @param {Object} options - Chart.js options object
 * @param {Object} options.plugins.verticalLinePlugin - Threshold plugin configuration:
 *   - enabled: Boolean to enable threshold areas
 *   - events: Array of threshold event objects
 * @returns {Array} Updated series array with markArea data
 *
 * @example
 * const seriesWithThresholds = createThresholdMarkAreas(series, {
 *   plugins: {
 *     verticalLinePlugin: {
 *       enabled: true,
 *       events: thresholdEvents
 *     }
 *   }
 * });
 */
export const createThresholdMarkAreas = (series, options) => {
  const verticalLinePlugin = options?.plugins?.verticalLinePlugin;
  if (!verticalLinePlugin?.enabled) {
    return series;
  }

  const updatedSeries = [...series];

  // Process each series to add markArea for threshold events
  updatedSeries.forEach((seriesItem) => {
    const rawData = seriesItem._rawData;
    if (!rawData || rawData.length === 0) return;

    // Group by threshold level for proper coloring
    const threshold1Areas = [];
    const threshold2Areas = [];
    const threshold3Areas = [];

    rawData.forEach((point, index) => {
      const thresholdLevel = point.redLine || 0;
      if (thresholdLevel > 0) {
        let eventStartIdx = index;
        let eventEndIdx = index;

        // Find extent of this threshold event
        while (
          eventEndIdx < rawData.length - 1 &&
          (rawData[eventEndIdx + 1].redLine || 0) === thresholdLevel
        ) {
          eventEndIdx++;
        }

        const areaData = [
          { xAxis: eventStartIdx, yAxis: 'min', itemStyle: { color: 'transparent' } },
          { xAxis: eventEndIdx, yAxis: 'max', itemStyle: { color: 'transparent' } },
        ];

        if (thresholdLevel === 3) threshold3Areas.push(areaData);
        else if (thresholdLevel === 2) threshold2Areas.push(areaData);
        else if (thresholdLevel === 1) threshold1Areas.push(areaData);
      }
    });

    const allMarkAreas = [
      ...threshold1Areas.map((area) =>
        [...area].map((item, i) =>
          i === 1 ? { ...item, itemStyle: { color: 'rgba(255, 255, 0, 0.2)' } } : item,
        ),
      ),
      ...threshold2Areas.map((area) =>
        [...area].map((item, i) =>
          i === 1 ? { ...item, itemStyle: { color: 'rgba(255, 165, 0, 0.25)' } } : item,
        ),
      ),
      ...threshold3Areas.map((area) =>
        [...area].map((item, i) =>
          i === 1 ? { ...item, itemStyle: { color: 'rgba(255, 0, 0, 0.3)' } } : item,
        ),
      ),
    ];

    if (allMarkAreas.length > 0) {
      seriesItem.markArea = {
        silent: true,
        data: allMarkAreas,
      };
    }
  });

  return updatedSeries;
};

/**
 * Creates mark points with weather condition icons rendered from react-icons.
 * Converts React components to SVG data URLs for ECharts display.
 *
 * @param {Array} series - ECharts series array with _rawData
 * @param {Object} options - Chart.js options object
 * @param {Object} options.plugins.weatherIconAtPoint - Weather icon plugin configuration:
 *   - enabled: Boolean to enable weather icons
 *   - datasets: Array of dataset indices to apply icons to
 *   - iconIds: Array of weather condition icon IDs
 *   - iconStyle: Style object with color property (defaults to black)
 *   - iconSize: Size of icons in pixels (default: 35)
 *   - iconOffset: [x, y] offset array (default: [0, 0])
 *   - step: Number of data points to skip between icons (default: 0 = show all)
 * @returns {Array} Updated series array with markPoint data containing weather icons
 *
 * @example
 * const seriesWithIcons = createWeatherIconMarkPoints(series, {
 *   plugins: {
 *     weatherIconAtPoint: {
 *       enabled: true,
 *       datasets: [0],
 *       iconIds: data.map(item => item.icon),
 *       iconStyle: { color: 'black' },
 *       iconSize: 35
 *     }
 *   }
 * });
 */
export const createWeatherIconMarkPoints = (series, options) => {
  const weatherIconPlugin = options?.plugins?.weatherIconAtPoint;
  if (!weatherIconPlugin?.enabled || !weatherIconPlugin.datasets) {
    return series;
  }

  const updatedSeries = [...series];

  weatherIconPlugin.datasets.forEach((datasetIndex) => {
    if (updatedSeries[datasetIndex] && weatherIconPlugin.iconIds) {
      const rawData = updatedSeries[datasetIndex]._rawData;
      const data = updatedSeries[datasetIndex].data;

      if (!rawData || rawData.length === 0) {
        console.warn('WeatherIconPlugin: No raw data available for dataset', datasetIndex);
        return;
      }

      if (!weatherIconPlugin.iconIds || weatherIconPlugin.iconIds.length === 0) {
        console.warn('WeatherIconPlugin: No iconIds provided');
        return;
      }

      const markPointData = [];
      rawData.forEach((point, index) => {
        if (index < weatherIconPlugin.iconIds.length && data[index] != null) {
          const iconId = weatherIconPlugin.iconIds[index];

          // Skip if no icon ID
          if (!iconId && iconId !== 0) {
            return;
          }

          // Skip rendering based on step
          if (weatherIconPlugin.step !== undefined && index % (weatherIconPlugin.step + 1) !== 0) {
            return;
          }

          try {
            // Create SVG for weather icon with explicit black color
            const iconSize = weatherIconPlugin.iconSize || 35;
            const iconStyle = {
              ...(weatherIconPlugin.iconStyle || {}),
              color: 'black',
              fill: 'black',
            };
            const iconSvg = renderToStaticMarkup(
              <ConditionIcons conditionId={iconId} size={iconSize} style={iconStyle} />,
            );
            const svgUrl = `data:image/svg+xml,${encodeURIComponent(iconSvg)}`;

            markPointData.push({
              coord: [index, data[index]],
              symbol: `image://${svgUrl}`,
              symbolSize: iconSize,
              symbolOffset: weatherIconPlugin.iconOffset || [0, 0],
            });
          } catch (error) {
            console.error('Error creating weather icon SVG:', error, { iconId, index });
          }
        }
      });

      if (markPointData.length > 0) {
        updatedSeries[datasetIndex].markPoint = {
          data: markPointData,
          symbol: 'image',
          symbolSize: weatherIconPlugin.iconSize || 35,
          label: { show: false },
        };
      } else {
        console.warn('WeatherIconPlugin: No mark points created for dataset', datasetIndex, {
          rawDataLength: rawData?.length,
          iconIdsLength: weatherIconPlugin.iconIds?.length,
          dataLength: data?.length,
        });
      }
    }
  });

  return updatedSeries;
};

/**
 * Creates mark points with wind direction arrows rotated based on wind direction.
 * Arrows are SVG graphics rotated to match wind direction in degrees.
 *
 * @param {Array} series - ECharts series array with _rawData
 * @param {Object} options - Chart.js options object
 * @param {Object} options.plugins.windDirectionIcon - Wind direction plugin configuration:
 *   - enabled: Boolean to enable wind arrows
 *   - datasets: Array of dataset indices to apply arrows to
 *   - iconIds: Optional array of wind direction values (fallback if not in rawData)
 *   - iconStyle: Style object with color property
 *   - iconSize: Size of arrows in pixels (default: 50)
 *   - iconOffset: [x, y] offset array (default: [0, -30])
 *   - step: Number of data points to skip between arrows (default: 0 = show all)
 * @returns {Array} Updated series array with markPoint data containing wind arrows
 *
 * @description
 * Supports multiple data format properties for wind direction:
 * - point.wind_dir
 * - point.windDirection
 * - point.wind_direction
 * - windPlugin.iconIds[index] (fallback)
 *
 * @example
 * const seriesWithArrows = createWindDirectionMarkPoints(series, {
 *   plugins: {
 *     windDirectionIcon: {
 *       enabled: true,
 *       datasets: [3],
 *       iconIds: data.map(item => item.wind_dir),
 *       iconSize: 50,
 *       iconOffset: [0, -30]
 *     }
 *   }
 * });
 */
export const createWindDirectionMarkPoints = (series, options) => {
  const windPlugin = options?.plugins?.windDirectionIcon;
  if (!windPlugin?.enabled || !windPlugin.datasets) {
    return series;
  }

  const updatedSeries = [...series];

  windPlugin.datasets.forEach((datasetIndex) => {
    if (updatedSeries[datasetIndex]) {
      const rawData = updatedSeries[datasetIndex]._rawData;
      const data = updatedSeries[datasetIndex].data;

      if (!rawData || rawData.length === 0) {
        console.warn('WindDirectionPlugin: No raw data available for dataset', datasetIndex);
        return;
      }

      const markPointData = [];
      rawData.forEach((point, index) => {
        // Support multiple formats for wind direction data
        let windDirection;
        if (typeof point === 'object') {
          windDirection = point?.wind_dir ?? point?.windDirection ?? point?.wind_direction;
        } else if (windPlugin.iconIds && windPlugin.iconIds[index] !== undefined) {
          // Fallback to iconIds array if provided
          windDirection = windPlugin.iconIds[index];
        }

        if (windDirection !== undefined && windDirection !== null && data[index] != null) {
          // Skip rendering based on step
          if (windPlugin.step !== undefined && index % (windPlugin.step + 1) !== 0) {
            return;
          }

          try {
            // Create SVG for wind direction arrow
            const arrowSize = windPlugin.iconSize || 50;
            const arrowSvg = `<svg width="${arrowSize}" height="${arrowSize}" viewBox="0 0 30 30" xmlns="http://www.w3.org/2000/svg" style="transform: rotate(${windDirection}deg)">
              <path d="M9.95,10.87c-0.01,0.35,0.1,0.65,0.34,0.9s0.53,0.37,0.89,0.36c0.34,0.02,0.63-0.1,0.88-0.37l1.66-1.64v10.3
                c-0.01,0.35,0.11,0.64,0.36,0.88s0.55,0.35,0.92,0.34c0.34,0.02,0.64-0.09,0.89-0.32s0.39-0.53,0.4-0.88v-10.3l1.64,1.64
                c0.23,0.24,0.53,0.37,0.88,0.37c0.36,0,0.66-0.12,0.9-0.36s0.36-0.53,0.36-0.89c-0.02-0.36-0.15-0.64-0.4-0.85l-3.74-3.84
                c-0.24-0.23-0.55-0.37-0.92-0.4c-0.37,0.02-0.68,0.16-0.92,0.41l-3.75,3.81C10.08,10.25,9.95,10.53,9.95,10.87z" 
                fill="black" stroke="black" stroke-width="0.5"/>
            </svg>`;
            const svgUrl = `data:image/svg+xml,${encodeURIComponent(arrowSvg)}`;

            // Use the actual data value for Y position (works with dedicated yWindDir axis)
            markPointData.push({
              coord: [index, data[index]],
              symbol: `image://${svgUrl}`,
              symbolSize: arrowSize,
              symbolOffset: windPlugin.iconOffset || [0, -30],
              itemStyle: {
                color: 'black',
              },
            });
          } catch (error) {
            console.error('Error creating wind direction SVG:', error, { windDirection, index });
          }
        }
      });

      if (markPointData.length > 0) {
        updatedSeries[datasetIndex].markPoint = {
          ...updatedSeries[datasetIndex].markPoint,
          data: [...(updatedSeries[datasetIndex].markPoint?.data || []), ...markPointData],
          symbol: 'image',
          symbolSize: windPlugin.iconSize || 50,
          label: { show: false },
        };
      } else {
        console.warn('WindDirectionPlugin: No mark points created for dataset', datasetIndex, {
          rawDataLength: rawData?.length,
          samplePoint: rawData[0],
        });
      }
    }
  });

  return updatedSeries;
};

/**
 * Creates vertical threshold indicator lines at the bottom of the chart.
 * These lines show when data points exceed threshold levels.
 *
 * @param {Array} series - ECharts series array with _rawData
 * @param {Object} options - Chart.js options object
 * @returns {Array} Graphic elements array for threshold lines
 *
 * @example
 * const graphics = createVerticalThresholdLines(series, options);
 */
export const createVerticalThresholdLines = (series, options) => {
  const verticalLinePlugin = options?.plugins?.verticalLinePlugin;
  if (!verticalLinePlugin?.enabled) {
    return [];
  }

  const graphics = [];
  const colors = ['#ffCC00', '#ff4400', '#ff0000']; // Yellow, Orange, Red

  series.forEach((seriesItem) => {
    const rawData = seriesItem._rawData;
    if (!rawData || rawData.length === 0) return;

    rawData.forEach((point, index) => {
      const thresholdLevel = point.redLine || 0;
      if (thresholdLevel > 0 && thresholdLevel <= 3) {
        graphics.push({
          type: 'line',
          shape: {
            x1: index,
            y1: '100%', // Bottom of chart
            x2: index,
            y2: '100%', // Start at bottom
          },
          style: {
            stroke: colors[thresholdLevel - 1],
            lineWidth: 3,
          },
          // Use coordinate system
          left: index,
          bottom: 0,
          z: 100,
        });
      }
    });
  });

  return graphics;
};

/**
 * Composite function that applies all mark point plugins in sequence.
 * Combines weather icons and wind direction arrows into series mark points.
 *
 * This is the main entry point for applying visual overlays to charts.
 *
 * @param {Array} series - ECharts series array with _rawData
 * @param {Object} options - Chart.js options object with plugin configurations
 * @returns {Array} Series array with both weather icon and wind direction mark points applied
 *
 * @example
 * const enhancedSeries = createMarkPoints(series, {
 *   plugins: {
 *     weatherIconAtPoint: {
 *       enabled: true,
 *       datasets: [0],
 *       iconIds: weatherIcons
 *     },
 *     windDirectionIcon: {
 *       enabled: true,
 *       datasets: [3],
 *       iconIds: windDirections
 *     }
 *   }
 * });
 */
export const createMarkPoints = (series, options) => {
  let updatedSeries = [...series];

  // Apply weather icons
  updatedSeries = createWeatherIconMarkPoints(updatedSeries, options);

  // Apply wind direction arrows
  updatedSeries = createWindDirectionMarkPoints(updatedSeries, options);

  return updatedSeries;
};
